import { inject, Injectable } from '@angular/core';
import { SINGLE_QUOTES_REGEX } from '@common/constants';
import { ContactODataItem } from '@common/models';
import { SortOrderByPipe } from '@common/pipes';
import { ContactsService } from '@common/services';
import { getUniqueListByProp } from '@common/services/smart-search';
import { ContactsTag, ContactsTagsMap } from '@common/services/smart-search/contacts/smart-search-tags-contacts.model';
import SmartSearchTemplatesBase from '@common/services/smart-search/smart-search-template-base';
import { FilterOptions } from '@common/types';
import { getFilterByContains } from '@common/utils/functions/search';
import { ModelTemplate, SmartSearchModelField } from 'mbs-ui-kit';
import { asapScheduler, Observable, scheduled } from 'rxjs';
import { map } from 'rxjs/operators';

@Injectable()
export class SmartSearchModelTemplateContactsService extends SmartSearchTemplatesBase {
  private readonly contactsService = inject(ContactsService);
  private readonly sortOrderByPipe = inject<SortOrderByPipe<ContactODataItem>>(SortOrderByPipe);

  readonly #firstName = ContactsTagsMap.get(ContactsTag.FirstName);
  readonly #lastName = ContactsTagsMap.get(ContactsTag.LastName);
  readonly #phoneNumber = ContactsTagsMap.get(ContactsTag.PhoneNumber);
  readonly #primaryEmail = ContactsTagsMap.get(ContactsTag.PrimaryEmail);

  public readonly FirstNameTag: ModelTemplate<ContactODataItem> = {
    tag: this.#firstName.tag,
    items: (state): Observable<ContactODataItem[]> => {
      const term = state.leftCaretValue?.replace(SINGLE_QUOTES_REGEX, '');
      const options: FilterOptions = {
        model: [{ value: term }] as SmartSearchModelField[],
        prop: this.#firstName.prop
      };

      this.contactsService.filter = term
        ? this.contactsService.convertFiltersToStr(this.#firstName.prop, getFilterByContains(options))
        : '';

      return scheduled(
        this.contactsService.getContactsItemsByGroupId(this.contactsService.groupId).pipe(
          map((res) => res.data),
          map((contacts) => {
            const filtered = contacts.filter((contact) => contact.FirstName != null && contact.FirstName.includes(term));
            const uniqueList = getUniqueListByProp(filtered, this.#firstName.prop).slice(0, 10);

            return this.sortOrderByPipe.transform(uniqueList, this.#firstName.prop);
          })
        ),
        asapScheduler
      );
    },
    itemFormatter: ({ FirstName }): string => FirstName,
    addGroupBrackets: true
  };

  public readonly LastNameTag: ModelTemplate<ContactODataItem> = {
    tag: this.#lastName.tag,
    items: (state): Observable<ContactODataItem[]> => {
      const term = state.leftCaretValue?.replace(SINGLE_QUOTES_REGEX, '');
      const options: FilterOptions = {
        model: [{ value: term }] as SmartSearchModelField[],
        prop: this.#lastName.prop
      };

      this.contactsService.filter = term ? this.contactsService.convertFiltersToStr(this.#lastName.prop, getFilterByContains(options)) : '';

      return scheduled(
        this.contactsService.getContactsItemsByGroupId(this.contactsService.groupId).pipe(
          map((res) => res.data),
          map((contacts) => {
            const filtered = contacts.filter((contact) => contact.LastName != null && contact.LastName.includes(term));
            const uniqueList = getUniqueListByProp(filtered, this.#lastName.prop).slice(0, 10);

            return this.sortOrderByPipe.transform(uniqueList, this.#lastName.prop);
          })
        ),
        asapScheduler
      );
    },
    itemFormatter: ({ LastName }): string => LastName,
    addGroupBrackets: true
  };

  public readonly PrimaryEmailTag: ModelTemplate<ContactODataItem> = {
    tag: this.#primaryEmail.tag,
    items: (state): Observable<ContactODataItem[]> => {
      const term = state.leftCaretValue?.replace(SINGLE_QUOTES_REGEX, '');
      const options: FilterOptions = {
        model: [{ value: term }] as SmartSearchModelField[],
        prop: this.#primaryEmail.prop
      };

      this.contactsService.filter = term
        ? this.contactsService.convertFiltersToStr(this.#primaryEmail.prop, getFilterByContains(options))
        : '';

      return scheduled(
        this.contactsService.getContactsItemsByGroupId(this.contactsService.groupId).pipe(
          map((res) => res.data),
          map((contacts) => {
            const filtered = contacts.filter((contact) => contact.PrimaryEmail != null && contact.PrimaryEmail.includes(term));
            const uniqueList = getUniqueListByProp(filtered, this.#primaryEmail.prop).slice(0, 10);

            return this.sortOrderByPipe.transform(uniqueList, this.#primaryEmail.prop);
          })
        ),
        asapScheduler
      );
    },
    itemFormatter: ({ PrimaryEmail }): string => PrimaryEmail,
    addGroupBrackets: true
  };

  public readonly PhoneNumberTag: ModelTemplate<ContactODataItem> = {
    tag: this.#phoneNumber.tag,
    items: (state): Observable<ContactODataItem[]> => {
      const term = state.leftCaretValue?.replace(SINGLE_QUOTES_REGEX, '');
      const options: FilterOptions = {
        model: [{ value: term }] as SmartSearchModelField[],
        prop: this.#phoneNumber.prop
      };

      this.contactsService.filter = term
        ? this.contactsService.convertFiltersToStr(this.#phoneNumber.prop, getFilterByContains(options))
        : '';

      return scheduled(
        this.contactsService.getContactsItemsByGroupId(this.contactsService.groupId).pipe(
          map((res) => res.data),
          map((contacts) => {
            const filtered = contacts.filter((contact) => contact.PhoneNumber != null && contact.PhoneNumber.includes(term));
            const uniqueList = getUniqueListByProp(filtered, this.#phoneNumber.prop).slice(0, 10);

            return this.sortOrderByPipe.transform(uniqueList, this.#phoneNumber.prop);
          })
        ),
        asapScheduler
      );
    },
    itemFormatter: ({ PhoneNumber }): string => PhoneNumber,
    addGroupBrackets: true
  };
}
